// cache-bust: v1.3
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Skeleton,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  alpha,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Slider,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import axiosInstance, { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';


import ProjectAssignmentModal from 'src/sections/project-assign/ProjectAssignmentModal';

const findParentInTree = (node, childId, parentNode = null) => {
  if (!node) return null;
  const nUserId = (node.userId?._id || node.userId || node.id)?.toString();
  if (nUserId === childId.toString()) {
    return parentNode;
  }
  if (node.children) {
    let found = null;
    node.children.some((child) => {
      found = findParentInTree(child, childId, node);
      return !!found;
    });
    if (found) return found;
  }
  return null;
};

const getDescendantsOf = (node, targetId) => {
  if (!node || !targetId) return [];

  const findNode = (n, id) => {
    if (!n) return null;
    const nUserId = n.userId?._id || n.userId || n.id;
    if (nUserId && nUserId.toString() === id.toString()) return n;
    if (n.children) {
      let found = null;
      n.children.some(child => {
        found = findNode(child, id);
        return !!found;
      });
      if (found) return found;
    }
    return null;
  };

  const targetNode = findNode(node, targetId);
  if (!targetNode) return [];

  const collectIds = (n, list = []) => {
    if (!n) return list;
    if (n.children) {
      n.children.forEach(child => {
        const cUserId = child.userId?._id || child.userId || child.id;
        if (cUserId) {
          list.push(cUserId.toString());
        }
        collectIds(child, list);
      });
    }
    return list;
  };

  return collectIds(targetNode);
};

const findParentNameInTree = (node, targetId, parentName = 'Root') => {
  if (!node || !targetId) return null;
  const nUserId = node.userId?._id || node.userId || node.id;
  if (nUserId && nUserId.toString() === targetId.toString()) return parentName;
  if (node.children) {
    let result = null;
    node.children.some(child => {
      result = findParentNameInTree(child, targetId, node.name || 'Unknown');
      return !!result;
    });
    if (result) return result;
  }
  return null;
};

const checkIsOutOfLocation = (candidate, project) => {
  if (!project || (!project.states?.length && !project.cities?.length)) {
    return false;
  }

  const projectStateIds = new Set((project.states || []).map((s) => (s._id || s.id || s).toString()));
  const projectCityIds = new Set((project.cities || []).map((c) => (c._id || c.id || c).toString()));

  const userStateIds = (candidate.positioningStates || []).map((s) => (s._id || s.id || s).toString());
  if (candidate.positioningState) {
    userStateIds.push((candidate.positioningState._id || candidate.positioningState.id || candidate.positioningState).toString());
  }

  const userCityIds = (candidate.positioningCities || []).map((c) => (c._id || c.id || c).toString());
  if (candidate.positioningCity) {
    userCityIds.push((candidate.positioningCity._id || candidate.positioningCity.id || candidate.positioningCity).toString());
  }

  if (project.states?.length > 0) {
    const stateOverlap = userStateIds.some((id) => projectStateIds.has(id));
    if (!stateOverlap) return true;
  }

  if (project.cities?.length > 0) {
    const cityOverlap = userCityIds.some((id) => projectCityIds.has(id));
    if (!cityOverlap) return true;
  }

  return false;
};

export default function FormReporteeReassignmentSelector({
  name,
  label,
  required = false,
  disabled = false,
  helperText = '',
  EntityCreationDialog,
  ...other
}) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  
  const theme = useTheme();

  // Watch the selected employee being migrated
  const selectedUser = watch('userId');
  const migrationType = watch('migrationType');
  const toDesignation = watch('toDesignation');

  const targetUserId = useMemo(() => {
    if (!selectedUser) return null;
    return typeof selectedUser === 'object' && selectedUser ? selectedUser._id || selectedUser.id : selectedUser;
  }, [selectedUser]);

  const [reportees, setReportees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [affectedProjects, setAffectedProjects] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [bulkDirectManager, setBulkDirectManager] = useState(null);
  const [bulkProjectManagers, setBulkProjectManagers] = useState({});
  const [hierarchyModalProject, setHierarchyModalProject] = useState(null);

  const handleSaveStaged = (projectId, payload) => {
    const currentStaged = watch('stagedProjectAssignments') || [];
    const updatedStaged = [...currentStaged];
    const stagedIndex = updatedStaged.findIndex((spa) => spa.projectId === projectId);
    
    const newStagedItem = {
      projectId,
      hierarchyMode: payload.hierarchyMode,
      hierarchyTree: payload.hierarchyTree,
      designationLevels: payload.designationLevels,
      formCreators: payload.formCreators,
      stagedFormRights: payload.stagedFormRights,
    };
    
    if (stagedIndex !== -1) {
      updatedStaged[stagedIndex] = newStagedItem;
    } else {
      updatedStaged.push(newStagedItem);
    }
    setValue('stagedProjectAssignments', updatedStaged, { shouldValidate: true });

    // Also update projectReassignments for backend execution cycle/drift checks
    const currentProjReassigns = watch('projectReassignments') || [];
    const updatedProjReassigns = currentProjReassigns.filter((pr) => pr.projectId !== projectId);

    if (payload.hierarchyMode === 'user_based' && payload.hierarchyTree) {
      const projectObj = affectedProjects.find((p) => p.projectId === projectId);
      if (projectObj && projectObj.subordinates) {
        projectObj.subordinates.forEach((sub) => {
          const subId = sub._id || sub.id;
          if (subId) {
            const parentNode = findParentInTree(payload.hierarchyTree, subId);
            if (parentNode) {
              const toManagerId = parentNode.userId?._id || parentNode.userId;
              if (toManagerId && toManagerId.toString() !== targetUserId.toString()) {
                updatedProjReassigns.push({
                  projectId,
                  reporteeId: subId,
                  toManagerId,
                });
              }
            }
          }
        });
      }
    }
    setValue('projectReassignments', updatedProjReassigns, { shouldValidate: true });
  };

  const targetUserFromManagers = useMemo(() => {
    if (!targetUserId || !managers.length) return null;
    return managers.find((m) => m._id === targetUserId) || null;
  }, [targetUserId, managers]);

  const targetUserName = useMemo(() => {
    if (targetUserFromManagers) {
      return `${targetUserFromManagers.firstName || ''} ${targetUserFromManagers.lastName || ''}`.trim();
    }
    if (selectedUser && typeof selectedUser === 'object') {
      return `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.label || '';
    }
    return 'Employee';
  }, [selectedUser, targetUserFromManagers]);

  const toDesignationName = useMemo(() => {
    if (!toDesignation) return '';
    const targetIdStr = (toDesignation._id || toDesignation.id || toDesignation || '').toString();
    if (!targetIdStr) return '';
    const found = designations.find((d) => (d._id || d.id || '').toString() === targetIdStr);
    return found ? found.designation_name : '';
  }, [toDesignation, designations]);

  const eligibleBulkManagers = useMemo(() => {
    const reporteeIds = new Set(reportees.map((r) => r._id));
    return managers.filter(
      (m) => (migrationType === 'resignation_exit' ? m._id !== targetUserId : true) && !reporteeIds.has(m._id)
    );
  }, [managers, reportees, targetUserId, migrationType]);

  const totalProjectReassignmentsRequired = useMemo(
    () =>
      affectedProjects.reduce((acc, p) => {
        if (p.hierarchyMode === 'designation_based') {
          const vacantNodes = (p.affectedNodes || []).filter((n) => n.staffCount === 1);
          return acc + vacantNodes.length;
        }
        if (p.isLeaf) {
          return acc + 1; // 1 replacement user required
        }
        return acc + (p.subordinatesCount || 0); // reassign all reportees
      }, 0),
    [affectedProjects]
  );

  const handleBulkProjectReassign = (project, newValue, field) => {
    setBulkProjectManagers((prev) => ({
      ...prev,
      [project.projectId]: newValue,
    }));

    const currentAssignments = Array.isArray(field.value) ? field.value : [];
    const otherAssignments = currentAssignments.filter(
      (a) => a.projectId !== project.projectId
    );

    if (newValue) {
      const newAssignments = (project.subordinates || []).map((sub) => ({
        projectId: project.projectId,
        reporteeId: sub._id,
        toManagerId: newValue._id,
      }));
      field.onChange([...otherAssignments, ...newAssignments]);
    } else {
      field.onChange(otherAssignments);
    }
  };

  // Fetch reportees when target employee changes
  useEffect(() => {
    const fetchReporteesAndManagers = async () => {
      if (!targetUserId) {
        setReportees([]);
        setAffectedProjects([]);
        setDesignations([]);
        setValue(name, []);
        setValue('projectReassignments', []);
        setBulkDirectManager(null);
        setBulkProjectManagers({});
        return;
      }

      try {
        setLoading(true);
        setProjectsLoading(true);
        setError(null);

        // Fetch active designations
        try {
          const desResponse = await axiosInstance.post(endpoints.designationMaster.list, { limit: 0 });
          setDesignations(desResponse.data?.data || []);
        } catch (desErr) {
          console.error('Failed to fetch designations:', desErr);
        }

        // 1. Fetch subordinates (active employees reporting to targetUserId)
        const reporteesResponse = await axiosInstance.post(endpoints.users.list, {
          status: 'active',
          limit: 0,
          reportingManager: targetUserId,
          selectedFields: ['firstName', 'lastName', 'employeeId', 'reportingManager'],
        });
        const reporteesList = reporteesResponse.data?.data || [];
        setReportees(reporteesList);

        // Initialize empty assignments list in react-hook-form
        setValue(name, []);
        setValue('projectReassignments', []);
        setBulkDirectManager(null);
        setBulkProjectManagers({});

        // 2. Fetch all active employees who can be managers
        const managersResponse = await axiosInstance.post(endpoints.users.list, {
          status: 'active',
          limit: 0,
          selectedFields: [
            'firstName',
            'lastName',
            'employeeId',
            'designation',
            'positioningState',
            'positioningCity',
            'positioningStates',
            'positioningCities'
          ],
        });
        const allUsers = managersResponse.data?.data || [];
        setManagers(allUsers);

        // 3. Fetch affected projects and detailed project subordinates from pre-resignation check
        try {
          const projectsRes = await axiosInstance.get(`/api/admin/staff-migrations/pre-resignation-check/${targetUserId}`);
          if (projectsRes.data?.success) {
            setAffectedProjects(projectsRes.data.projectsAffected || []);
            if (projectsRes.data.directSubordinates) {
              setReportees(projectsRes.data.directSubordinates);
            }
          }
        } catch (projErr) {
          console.error('Failed to fetch affected projects:', projErr);
        }
      } catch (err) {
        console.error('Failed to fetch staff migration reassignments data:', err);
        setError('Failed to load employee subordinates and managers.');
      } finally {
        setLoading(false);
        setProjectsLoading(false);
      }
    };

    fetchReporteesAndManagers();
  }, [targetUserId, name, setValue]);

  const renderOptionWithLocation = (props, option, project = null, subordinateId = null) => {
    const isOutOfLoc = project ? checkIsOutOfLocation(option, project) : false;
    
    let isDescendant = false;
    if (project && project.hierarchyTree && subordinateId && option._id) {
      const descendants = getDescendantsOf(project.hierarchyTree, subordinateId);
      isDescendant = descendants.includes(option._id.toString());
    }

    let parentTreeText = '';
    if (project && project.hierarchyTree && option._id) {
      const parentName = findParentNameInTree(project.hierarchyTree, option._id);
      if (parentName) {
        parentTreeText = `🌳 Reports to: ${parentName}`;
      }
    }
    
    // Extract locations to show textually
    const locations = [];
    if (option.positioningState?.state_name) locations.push(option.positioningState.state_name);
    if (option.positioningCity?.city_name) locations.push(option.positioningCity.city_name);
    (option.positioningStates || []).forEach(s => {
      if (s.state_name && !locations.includes(s.state_name)) locations.push(s.state_name);
    });
    (option.positioningCities || []).forEach(c => {
      if (c.city_name && !locations.includes(c.city_name)) locations.push(c.city_name);
    });
    const locStr = locations.length > 0 ? `📍 ${locations.join(', ')}` : '📍 No Location';

    return (
      <li {...props} key={option._id}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {`${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {isOutOfLoc && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 0.5,
                    color: 'warning.contrastText',
                    bgcolor: 'warning.main',
                    fontWeight: 700,
                    fontSize: '0.65rem'
                  }}
                >
                  Location Mismatch
                </Typography>
              )}
              {isDescendant && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 0.5,
                    color: 'error.contrastText',
                    bgcolor: 'error.main',
                    fontWeight: 700,
                    fontSize: '0.65rem'
                  }}
                >
                  Child Node (Invalid)
                </Typography>
              )}
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {option.designation?.designation_name && (
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                {option.designation.designation_name}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {locStr}
            </Typography>
            {parentTreeText && (
              <Typography variant="caption" color="info.main" sx={{ fontWeight: 600 }}>
                {parentTreeText}
              </Typography>
            )}
          </Box>
        </Box>
      </li>
    );
  };

  // Render a single direct reportee reassignment row
  const renderReassignmentRow = (reportee, field) => {
    const eligibleManagers = managers.filter(
      (m) => m._id !== reportee._id && (migrationType === 'resignation_exit' ? m._id !== targetUserId : true)
    );

    const currentAssignments = Array.isArray(field.value) ? field.value : [];
    const currentAssignment = currentAssignments.find((a) => a.reporteeId === reportee._id);
    const selectedManager = currentAssignment
      ? eligibleManagers.find((m) => m._id === currentAssignment.toManagerId) || null
      : null;

    const otherManagers = (reportee.reportingManager || []).filter(
      (m) => (m._id || m.id || m).toString() !== targetUserId.toString()
    );
    const hasOtherManagers = otherManagers.length > 0;

    return (
      <Card
        key={reportee._id}
        sx={{
          p: 2,
          mb: 1.5,
          border: 1,
          borderColor: selectedManager ? 'success.main' : alpha(theme.palette.divider, 0.6),
          bgcolor: selectedManager
            ? alpha(theme.palette.success.main, 0.03)
            : 'background.paper',
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: selectedManager ? 'success.main' : 'primary.main',
            boxShadow: theme.customShadows?.z4 || theme.shadows[2],
          },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Subordinate info */}
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <Iconify icon="solar:user-bold" width={20} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {`${reportee.firstName || ''} ${reportee.lastName || ''}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Employee ID: {reportee.employeeId || 'N/A'}
                </Typography>
                {hasOtherManagers && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      label="Multiple RM"
                      size="small"
                      color="info"
                      variant="soft"
                      sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }}
                    />
                    <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 600 }}>
                      Co-managed by: {otherManagers.map(m => `${m.firstName || ''} ${m.lastName || ''}`).join(', ')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Grid>

          {/* New Manager selection */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={eligibleManagers}
              value={selectedManager}
              onChange={(event, newValue) => {
                const otherAssignments = currentAssignments.filter((a) => a.reporteeId !== reportee._id);
                setBulkDirectManager(null);
                if (newValue) {
                  field.onChange([
                    ...otherAssignments,
                    { reporteeId: reportee._id, toManagerId: newValue._id },
                  ]);
                } else {
                  field.onChange(otherAssignments);
                }
              }}
              getOptionLabel={(option) =>
                `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`.trim()
              }
              renderOption={(props, option) => renderOptionWithLocation(props, option)}
              isOptionEqualToValue={(option, val) => option._id === val._id}
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={hasOtherManagers || migrationType !== 'resignation_exit' ? "Reassign Reporting Manager (Optional)" : "Select New Reporting Manager"}
                  placeholder={hasOtherManagers || migrationType !== 'resignation_exit' ? "Choose manager (optional)..." : "Choose new manager..."}
                  required={required && !hasOtherManagers && migrationType === 'resignation_exit'}
                  size="small"
                  error={required && !hasOtherManagers && migrationType === 'resignation_exit' && !selectedManager}
                />
              )}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  // Render a single project reassignment row
  const renderProjectReassignmentRow = (project, subordinate, field) => {
    const eligibleManagers = project.eligibleManagers || [];

    const currentAssignments = Array.isArray(field.value) ? field.value : [];
    const currentAssignment = currentAssignments.find(
      (a) => a.projectId === project.projectId && a.reporteeId === subordinate._id
    );
    const selectedManager = currentAssignment
      ? eligibleManagers.find((m) => m._id === currentAssignment.toManagerId) || null
      : null;

    return (
      <Card
        key={subordinate._id}
        sx={{
          p: 1.5,
          mb: 1,
          border: 1,
          borderColor: selectedManager ? 'success.main' : alpha(theme.palette.divider, 0.6),
          bgcolor: selectedManager
            ? alpha(theme.palette.success.main, 0.03)
            : 'background.paper',
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: selectedManager ? 'success.main' : 'primary.main',
          },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Subordinate info */}
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: 'info.main',
                }}
              >
                <Iconify icon="solar:user-bold" width={16} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {`${subordinate.firstName || ''} ${subordinate.lastName || ''}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Employee ID: {subordinate.employeeId || 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* New Manager selection */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={eligibleManagers}
              value={selectedManager}
              onChange={(event, newValue) => {
                const otherAssignments = currentAssignments.filter(
                  (a) => !(a.projectId === project.projectId && a.reporteeId === subordinate._id)
                );
                
                setBulkProjectManagers((prev) => ({
                  ...prev,
                  [project.projectId]: null,
                }));

                if (newValue) {
                  field.onChange([
                    ...otherAssignments,
                    { projectId: project.projectId, reporteeId: subordinate._id, toManagerId: newValue._id },
                  ]);
                } else {
                  field.onChange(otherAssignments);
                }
              }}
              getOptionLabel={(option) =>
                `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`.trim()
              }
              getOptionDisabled={(option) => {
                if (project && project.hierarchyTree && subordinate._id) {
                  const descendants = getDescendantsOf(project.hierarchyTree, subordinate._id);
                  if (descendants.includes(option._id?.toString())) {
                    return true;
                  }
                }
                return false;
              }}
              renderOption={(props, option) => renderOptionWithLocation(props, option, project, subordinate._id)}
              isOptionEqualToValue={(option, val) => option._id === val._id}
              disabled={disabled}
              renderInput={(params) => {
                const hasLocationMismatch = selectedManager ? checkIsOutOfLocation(selectedManager, project) : false;
                return (
                  <TextField
                    {...params}
                    label="Select New Reporting Manager"
                    placeholder="Choose new manager..."
                    required={required}
                    size="small"
                    error={(required && !selectedManager) || hasLocationMismatch}
                    helperText={
                      hasLocationMismatch ? (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                          ⚠️ Warning: Selected manager is located outside the project&apos;s work locations.
                        </Typography>
                      ) : ''
                    }
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderProjectLeafNodeReplacementRow = (project, field) => {
    const eligibleManagers = project.eligibleManagers || [];

    const currentAssignments = Array.isArray(field.value) ? field.value : [];
    const currentAssignment = currentAssignments.find(
      (a) => a.projectId === project.projectId && a.reporteeId === targetUserId
    );
    const selectedReplacement = currentAssignment
      ? eligibleManagers.find((m) => m._id === currentAssignment.toManagerId) || null
      : null;

    return (
      <Card
        key="leaf-replacement"
        sx={{
          p: 2,
          mb: 1,
          border: 1,
          borderColor: selectedReplacement ? 'success.main' : alpha(theme.palette.warning.main, 0.6),
          bgcolor: selectedReplacement
            ? alpha(theme.palette.success.main, 0.03)
            : alpha(theme.palette.warning.main, 0.02),
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: selectedReplacement ? 'success.main' : 'warning.main',
          },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Subordinate info */}
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.main',
                }}
              >
                <Iconify icon="solar:user-rounded-bold-duotone" width={16} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {targetUserName} (Leaf Node)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No subordinates in this project tree. Select a replacement to take their position in the visual structure.
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Replacement selection */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={eligibleManagers}
              value={selectedReplacement}
              onChange={(event, newValue) => {
                const otherAssignments = currentAssignments.filter(
                  (a) => !(a.projectId === project.projectId && a.reporteeId === targetUserId)
                );
                
                if (newValue) {
                  field.onChange([
                    ...otherAssignments,
                    { projectId: project.projectId, reporteeId: targetUserId, toManagerId: newValue._id },
                  ]);
                } else {
                  field.onChange(otherAssignments);
                }
              }}
              getOptionLabel={(option) =>
                `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`.trim()
              }
              renderOption={(props, option) => renderOptionWithLocation(props, option, project)}
              isOptionEqualToValue={(option, val) => option._id === val._id}
              disabled={disabled}
              renderInput={(params) => {
                const hasLocationMismatch = selectedReplacement ? checkIsOutOfLocation(selectedReplacement, project) : false;
                return (
                  <TextField
                    {...params}
                    label="Select Replacement Member"
                    placeholder="Choose replacement..."
                    required={required}
                    size="small"
                    error={(required && !selectedReplacement) || hasLocationMismatch}
                    helperText={
                      hasLocationMismatch ? (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                          ⚠️ Warning: Selected member is located outside the project&apos;s work locations.
                        </Typography>
                      ) : ''
                    }
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderProjectDesignationReplacementRow = (project, node, field) => {
    const nodeDesId = node.designationId?._id || node.designationId || '';
    const eligibleCandidates = managers.filter(
      (m) => m._id !== targetUserId && (m.designation?._id || m.designation || '').toString() === nodeDesId.toString()
    );

    const currentAssignments = Array.isArray(field.value) ? field.value : [];
    const currentAssignment = currentAssignments.find(
      (a) => a.projectId === project.projectId && a.reporteeId === targetUserId
    );
    const selectedReplacement = currentAssignment
      ? eligibleCandidates.find((m) => m._id === currentAssignment.toManagerId) || null
      : null;

    return (
      <Card
        key={node.nodeId || node.designationId}
        sx={{
          p: 2,
          mb: 1.5,
          border: 1,
          borderColor: selectedReplacement ? 'success.main' : alpha(theme.palette.warning.main, 0.6),
          bgcolor: selectedReplacement
            ? alpha(theme.palette.success.main, 0.03)
            : alpha(theme.palette.warning.main, 0.02),
          transition: 'all 0.25s ease',
          '&:hover': {
            borderColor: selectedReplacement ? 'success.main' : 'warning.main',
          },
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.main',
                }}
              >
                <Iconify icon="solar:shield-warning-bold-duotone" width={16} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  Vacancy Alert: {node.designationName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The leaving employee is the sole member of this designation in the project. Select a replacement with the same designation to fill this position.
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={eligibleCandidates}
              value={selectedReplacement}
              onChange={(event, newValue) => {
                const otherAssignments = currentAssignments.filter(
                  (a) => !(a.projectId === project.projectId && a.reporteeId === targetUserId)
                );
                if (newValue) {
                  field.onChange([
                    ...otherAssignments,
                    { projectId: project.projectId, reporteeId: targetUserId, toManagerId: newValue._id },
                  ]);
                } else {
                  field.onChange(otherAssignments);
                }
              }}
              getOptionLabel={(option) =>
                `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`.trim()
              }
              renderOption={(props, option) => renderOptionWithLocation(props, option, project)}
              isOptionEqualToValue={(option, val) => option._id === val._id}
              disabled={disabled}
              renderInput={(params) => {
                const hasLocationMismatch = selectedReplacement ? checkIsOutOfLocation(selectedReplacement, project) : false;
                return (
                  <TextField
                    {...params}
                    label="Select Replacement Employee"
                    placeholder="Choose replacement..."
                    required={required}
                    size="small"
                    error={(required && !selectedReplacement) || hasLocationMismatch}
                    helperText={
                      hasLocationMismatch ? (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontWeight: 600 }}>
                          ⚠️ Warning: Selected user is located outside the project&apos;s work locations.
                        </Typography>
                      ) : ''
                    }
                  />
                );
              }}
            />
          </Grid>
        </Grid>
      </Card>
    );
  };

  if (!targetUserId || migrationType === 'reporting_change') {
    return null;
  }

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      {/* 1. Direct Subordinates Controller */}
      <Controller
        name={name}
        control={control}
        rules={{
          validate: (value) => {
            if (migrationType !== 'resignation_exit') return true;
            if (!required || reportees.length === 0) return true;
            const currentAssignments = Array.isArray(value) ? value : [];
            
            // Check if any reportee who does NOT have other managers is missing a selection
            const missingReassignment = reportees.some((r) => {
              const otherManagers = (r.reportingManager || []).filter(
                (m) => (m._id || m.id || m).toString() !== targetUserId.toString()
              );
              if (otherManagers.length > 0) return false; // not required
              return !currentAssignments.some((a) => a.reporteeId === r._id);
            });

            if (missingReassignment) {
              return 'Please reassign all direct subordinates who do not have other active managers.';
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error: fieldError } }) => (
          <FormControl
            error={!!fieldError || !!errors[name]}
            required={required && migrationType === 'resignation_exit'}
            disabled={disabled}
            fullWidth
            {...other}
          >
            {label && (
              <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                {label} (Direct Profile Hierarchy)
              </FormLabel>
            )}

            {/* Loading */}
            {loading && (
              <Stack spacing={1.5}>
                {[1, 2].map((item) => (
                  <Skeleton key={item} variant="rounded" height={70} />
                ))}
              </Stack>
            )}

            {/* Error */}
            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Empty - Direct subordinate count is 0 */}
            {!loading && !error && reportees.length === 0 && (
              <Alert
                severity="info"
                icon={<Iconify icon="solar:info-circle-bold" />}
                sx={{
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px dashed ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                This employee has no direct profile subordinates. No profile reassignment is required.
              </Alert>
            )}

            {/* Subordinates to reassign */}
            {!loading && !error && reportees.length > 0 && (
              <Box>
                <Alert
                  severity="warning"
                  icon={<Iconify icon="solar:shield-warning-bold" />}
                  sx={{ mb: 2, borderRadius: 1.5 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {`Direct Subordinates Reassignment (${reportees.length} total)`}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {migrationType === 'resignation_exit'
                      ? 'Subordinates who only report to this manager must be explicitly reassigned. For subordinates with multiple reporting managers (co-managed), reassignment is optional.'
                      : 'Reassigning subordinates is optional. Subordinates not explicitly reassigned will continue to report to this manager.'}
                  </Typography>
                </Alert>

                {reportees.length > 1 && (
                  <Box
                    sx={{
                      mb: 2.5,
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          Bulk Reassign All Subordinates
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Select a manager to apply to all direct profile subordinates at once.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={eligibleBulkManagers}
                          value={bulkDirectManager}
                          onChange={(event, newValue) => {
                            setBulkDirectManager(newValue);
                            if (newValue) {
                              field.onChange(
                                reportees.map((r) => ({
                                  reporteeId: r._id,
                                  toManagerId: newValue._id,
                                }))
                              );
                            } else {
                              field.onChange([]);
                            }
                          }}
                          getOptionLabel={(option) =>
                            `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'N/A'})`.trim()
                          }
                          renderOption={(props, option) => renderOptionWithLocation(props, option)}
                          isOptionEqualToValue={(option, val) => option._id === val._id}
                          disabled={disabled}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bulk Select Manager"
                              placeholder="Choose manager..."
                              size="small"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Box sx={{ pr: 0.5 }}>
                  {reportees.map((reportee) => renderReassignmentRow(reportee, field))}
                </Box>
              </Box>
            )}

            {/* Errors / helper text */}
            {(error || errors[name] || fieldError?.message) && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors[name]?.message || fieldError?.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />

      {/* Divider if both exist */}
      {!loading && !error && (reportees.length > 0 || affectedProjects.length > 0) && (
        <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
      )}

      {/* 2. Project Subordinates Controller */}
      <Controller
        name="stagedProjectAssignments"
        control={control}
        rules={{
          validate: (value) => {
            if (!required || affectedProjects.length === 0) return true;
            const currentStaged = Array.isArray(value) ? value : [];
            if (currentStaged.length < affectedProjects.length) {
              return 'Please configure reassignments/replacements for all affected project hierarchies before proceeding.';
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error: projError } }) => (
          <FormControl
            error={!!projError || !!errors.stagedProjectAssignments}
            required={required}
            disabled={disabled}
            fullWidth
          >
            <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
              Project Hierarchies Reassignments
            </FormLabel>

            {/* Loading */}
            {projectsLoading && (
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={70} />
              </Stack>
            )}

            {/* Empty - No affected projects */}
            {!projectsLoading && !error && affectedProjects.length === 0 && (
              <Alert
                severity="info"
                icon={<Iconify icon="solar:info-circle-bold" />}
                sx={{
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px dashed ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                This employee has no active reporting roles in any project assignments. No project reassignment is required.
              </Alert>
            )}

            {/* Project subordinates to reassign */}
            {!projectsLoading && !error && affectedProjects.length > 0 && (
              <Box>
                <Alert
                  severity="warning"
                  icon={<Iconify icon="solar:folder-with-files-bold" />}
                  sx={{ mb: 2, borderRadius: 1.5 }}
                >
                  This employee has active reporting structures in the following projects. Reassigning/replacing them will affect these hierarchies:
                </Alert>

                <Stack spacing={2} sx={{ pr: 0.5 }}>
                  {affectedProjects.map((project) => {
                    const isFullyConfigured = (field.value || []).some(
                      (spa) => spa.projectId === project.projectId
                    );

                    return (
                      <Card
                        key={project.projectId}
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.grey[500], 0.02),
                          borderStyle: 'dashed',
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Iconify
                                icon={
                                  project.hierarchyMode === 'designation_based'
                                    ? 'solar:shield-user-bold-duotone'
                                    : 'solar:folder-bold-duotone'
                                }
                                sx={{ color: project.hierarchyMode === 'designation_based' ? 'warning.main' : 'info.main' }}
                                width={20}
                              />
                              <Typography
                                variant="subtitle2"
                                color={project.hierarchyMode === 'designation_based' ? 'warning.main' : 'info.main'}
                                sx={{ fontWeight: 600 }}
                              >
                                {project.projectName} {project.hierarchyMode === 'designation_based' ? '(Designation-Based)' : ''}
                              </Typography>
                            </Stack>

                            {/* Right side of project card header: view hierarchy button */}
                            <Stack direction="row" spacing={1} alignItems="center">
                              {project.hierarchyTree && (
                                <Tooltip title="Configure reassignments inside visual project assignment panel" arrow>
                                  <Button
                                    size="small"
                                    variant={isFullyConfigured ? 'soft' : 'contained'}
                                    color={isFullyConfigured ? 'success' : 'warning'}
                                    startIcon={<Iconify icon="solar:chart-2-bold-duotone" width={15} />}
                                    onClick={() => setHierarchyModalProject(project)}
                                    sx={{
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      borderRadius: 1.2,
                                      px: 1.25,
                                      py: 0.4,
                                      minHeight: 0,
                                      lineHeight: 1.6,
                                      textTransform: 'none',
                                    }}
                                  >
                                    {isFullyConfigured ? 'Edit Reassignments' : 'Configure Reassignments'}
                                  </Button>
                                </Tooltip>
                              )}
                            </Stack>
                          </Stack>

                          {/* Project locations metadata */}
                          {(project.states?.length > 0 || project.cities?.length > 0) && (
                            <Box sx={{ pl: 3.5, mt: -0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Project Locations: {
                                  [
                                    ...(project.states || []).map(s => s.state_name || s.name),
                                    ...(project.cities || []).map(c => c.city_name || c.name)
                                  ].join(', ')
                                }
                              </Typography>
                            </Box>
                          )}

                          <Stack spacing={1}>
                            {isFullyConfigured ? (
                              <Alert
                                severity="success"
                                icon={<Iconify icon="solar:check-circle-bold-duotone" />}
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.success.main, 0.02),
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                  Reassignments Configured
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  This project&apos;s hierarchy assignment has been configured. Click **&quot;Edit Reassignments&quot;** in the header to modify.
                                </Typography>
                              </Alert>
                            ) : (
                              <Alert
                                severity="warning"
                                icon={<Iconify icon="solar:shield-warning-bold-duotone" />}
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.warning.main, 0.02),
                                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main' }}>
                                  Reassignments Pending
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                  Please configure this project&apos;s assignments inside the visual hierarchy modal. Click **&quot;Configure Reassignments&quot;** above.
                                </Typography>
                              </Alert>
                            )}
                          </Stack>
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Errors / helper text */}
            {(errors.stagedProjectAssignments || projError?.message) && (
              <FormHelperText error sx={{ mt: 1 }}>
                {errors.stagedProjectAssignments?.message || projError?.message}
              </FormHelperText>
            )}
            {helperText && !error && !errors[name] && !errors.stagedProjectAssignments && (
              <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>
            )}

            {/* ── Project Assignment Modal ── */}
            {hierarchyModalProject && (
              <ProjectAssignmentModal
                open={!!hierarchyModalProject}
                onClose={() => setHierarchyModalProject(null)}
                project={{
                  _id: hierarchyModalProject?.projectId,
                  name: hierarchyModalProject?.projectName,
                  states: hierarchyModalProject?.states,
                  cities: hierarchyModalProject?.cities,
                }}
                isMigrationContext
                stagedAssignment={(field.value || []).find(
                  (spa) => spa.projectId === hierarchyModalProject?.projectId
                )}
                onSaveStaged={(payload) => handleSaveStaged(hierarchyModalProject.projectId, payload)}
                migrationInfo={{
                  targetUserId,
                  migrationType: migrationType || 'resignation_exit',
                  toStates: watch('toStates') || [],
                  toCities: watch('toCities') || [],
                }}
                EntityCreationDialog={EntityCreationDialog}
              />
            )}
          </FormControl>
        )}
      />
    </Stack>
  );
}

FormReporteeReassignmentSelector.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  EntityCreationDialog: PropTypes.elementType,
};
